import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { SERVICES } from "../lib/siteData";

export default function ServicesPage() {
    return (
        <div data-testid="page-services">
            <section className="pt-40 pb-16 max-w-7xl mx-auto px-6 md:px-10">
                <div className="eyebrow mb-6">What we do</div>
                <h1 className="font-display text-5xl md:text-7xl leading-[1] text-hhc-ink max-w-4xl">
                    Bespoke work, from concept to move-in day.
                </h1>
                <p className="mt-8 text-hhc-muted text-[17px] max-w-2xl leading-relaxed">
                    Every service is delivered by our in-house team — no
                    outsourced coordination, no lost details. Choose one, or
                    combine them for a turnkey home.
                </p>
            </section>

            <section className="max-w-7xl mx-auto px-6 md:px-10 pb-32">
                <div className="flex flex-col gap-28">
                    {SERVICES.map((s, idx) => {
                        const reverse = idx % 2 === 1;
                        return (
                            <article
                                key={s.slug}
                                id={s.slug}
                                data-testid={`service-detail-${s.slug}`}
                                className={`grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center scroll-mt-32 ${
                                    reverse ? "md:[direction:rtl]" : ""
                                }`}
                            >
                                <div
                                    className={`md:col-span-7 relative rounded-2xl overflow-hidden aspect-[16/11] hover-lift ${
                                        reverse ? "md:[direction:ltr]" : ""
                                    }`}
                                >
                                    <img
                                        src={s.image}
                                        alt={s.title}
                                        className="w-full h-full object-cover"
                                    />
                                    <span className="absolute top-6 left-6 bg-hhc-ivory/90 backdrop-blur rounded-full px-3 py-1 text-[10px] tracking-[0.28em] uppercase font-semibold text-hhc-clay">
                                        {String(idx + 1).padStart(2, "0")}
                                    </span>
                                </div>
                                <div
                                    className={`md:col-span-5 ${
                                        reverse ? "md:[direction:ltr]" : ""
                                    }`}
                                >
                                    <div className="eyebrow mb-4">Service</div>
                                    <h2 className="font-display text-4xl md:text-5xl leading-[1.05] text-hhc-ink">
                                        {s.title}
                                    </h2>
                                    <p className="mt-6 text-hhc-muted leading-[1.75]">
                                        {s.detail}
                                    </p>
                                    <ul className="mt-8 space-y-3">
                                        {s.deliverables.map((d) => (
                                            <li
                                                key={d}
                                                className="flex items-start gap-3 text-[15px] text-hhc-graphite"
                                            >
                                                <CheckCircle2
                                                    size={18}
                                                    className="mt-0.5 text-hhc-sage shrink-0"
                                                    strokeWidth={1.5}
                                                />
                                                <span>{d}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <Link
                                        to="/contact"
                                        className="btn-primary text-[13px] mt-10"
                                        data-testid={`service-cta-${s.slug}`}
                                    >
                                        Enquire about {s.title}
                                        <ArrowRight size={14} />
                                    </Link>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </section>

            <section className="bg-hhc-ink text-hhc-cream py-24">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-8">
                        <h3 className="font-display text-4xl md:text-5xl text-hhc-ivory leading-[1.05]">
                            Not sure where to start?
                        </h3>
                        <p className="mt-6 text-hhc-cream/70 max-w-lg">
                            A 20-minute site walk with our studio is free.
                            We'll tell you what's possible in your space, and
                            what's honestly not worth the spend.
                        </p>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <Link
                            to="/contact"
                            className="inline-flex items-center gap-2 bg-hhc-sun text-hhc-ink px-7 py-4 rounded-full font-medium hover:bg-hhc-ivory transition-colors"
                            data-testid="services-book-consult"
                        >
                            Book a consult
                            <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
